import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOutput(null);
    try {
      await login(formData);
      setOutput(
        <div className="mt-4 text-[#00ff66]">Welcome, <span className="font-bold">{formData.email}</span>! Redirecting to dashboard...</div>
      );
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error) {
      setOutput(<div className="mt-4 text-[#ff5555]">Login failed. Please check your credentials.</div>);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center py-8 sm:py-16 px-2 sm:px-4 md:px-8 bg-[#181a1b]" dir="ltr">
      <div className="w-full max-w-md rounded-xl shadow-2xl bg-[#23272e] overflow-hidden relative">
        {/* Terminal window bar */}
        <div className="terminal-window-bar relative z-10 text-left flex items-center px-2 sm:px-8 py-2">
          <svg className="h-5 w-5 text-[#39c5bb] mr-2" viewBox="0 0 20 20" fill="currentColor"><path d="M2 4.5A2.5 2.5 0 014.5 2h11A2.5 2.5 0 0118 4.5v11A2.5 2.5 0 0115.5 18h-11A2.5 2.5 0 012 15.5v-11zm2.75 3.25a.75.75 0 10-1.06 1.06l2.22 2.22-2.22 2.22a.75.75 0 101.06 1.06l2.75-2.75a.75.75 0 000-1.06l-2.75-2.75zm4.5 6.25a.75.75 0 000 1.5h4a.75.75 0 000-1.5h-4z" /></svg>
          <span className="text-base font-bold terminal-prompt select-none">user@blog:~$</span>
          <span className="ml-auto text-xs text-[#39c5bb] font-mono opacity-70">Login</span>
        </div>
        {/* Terminal content */}
        <div className="bg-[#23272e] px-2 sm:px-8 py-6 sm:py-12 flex flex-col gap-8 font-mono animate-fadein text-left min-h-[300px] overflow-x-auto">
          <div className="text-[#00ff66] text-base mb-2">
            # Please login to access the dashboard
          </div>
          {output}
          <form onSubmit={handleSubmit} className="terminal-output text-[#e5e5e5] flex flex-col gap-6">
            <div>
              <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Email</label>
              <input
                ref={inputRef}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-[#39c5bb] rounded-2xl focus:ring-2 focus:ring-[#39c5bb] focus:border-transparent transition-all duration-200 text-base bg-[#23272e] text-[#e5e5e5]"
                placeholder="Enter your email"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#e5e5e5] mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-[#39c5bb] rounded-2xl focus:ring-2 focus:ring-[#39c5bb] focus:border-transparent transition-all duration-200 text-base bg-[#23272e] text-[#e5e5e5]"
                placeholder="Enter your password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center px-4 py-3 bg-[#39c5bb] text-[#181a1b] rounded-xl hover:bg-[#00ff66] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl mt-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#181a1b] mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>
      </div>
      {/* Subtle background pattern */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-10" style={{background: 'radial-gradient(circle at 60% 40%, #00ff66 0%, transparent 60%)'}} />
      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: none; } }
        .animate-fadein { animation: fadein 0.7s cubic-bezier(.4,1.7,.6,.97) both; }
      `}</style>
    </div>
  );
};

export default Login; 